/**
 * Batch-review panel for pending refinance matches (WS: backlog clear).
 *
 * A dense, scannable table of PENDING matches with multi-select + one-click bulk
 * Confirm / Reject, so a reviewer can clear a large backlog (Decru ~389, Timms
 * ~191) fast instead of expanding one card at a time. Every decision is a human
 * choice - there is NO auto-confirm here; the panel just batches the reviewer's
 * selections into a single bulkVerifyRefinanceMatches call.
 *
 * Rows are sorted so the empirically-safe tier clusters at the top: an exact or
 * normalized name match with days_between != 0 (a real gap between discharge and
 * the new loan). The reviewer bulk-confirms those, then scrutinises the tail
 * (partial names / same-day) below the divider.
 *
 * On confirm the backend REQUIRES a relationshipType per row: each row defaults
 * to its persisted suggestedRelationshipType and the reviewer can flip Refinance
 * vs Returning Client inline; a Refinance additionally needs an outcome (default
 * Win-back). Dark-theme tokens match the surrounding Refinance Intelligence page.
 */
import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { CheckCircle, XCircle, Sparkles, Repeat, Users, ChevronRight } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';
import { useAuth } from '../../hooks/useAuth';
import { BROKER_FILTERED_ADMIN_COPY } from '../../utils/brokerFilteredAdmin';
import type {
  RefinanceMatch,
  RelationshipType,
  OutcomeType,
  BulkVerifyDecision,
} from '../../hooks/useRefinanceData';

const OUTCOME_OPTIONS: { value: OutcomeType; label: string }[] = [
  { value: 'win_back', label: 'Win-back' },
  { value: 'internal_transfer', label: 'Internal transfer' },
  { value: 'cross_template_conversion', label: 'Cross-book conversion' },
  { value: 'office_transfer', label: 'Office transfer' },
];

/** Empirically-safe tier: exact/normalized name AND a non-zero day gap. */
function isClearMatch(m: RefinanceMatch): boolean {
  return (m.matchType === 'exact' || m.matchType === 'normalized') && m.daysBetween !== 0;
}

interface BatchReviewPanelProps {
  companyId: string;
  /** Pending matches currently loaded (the parent filters to the pending tab). */
  matches: RefinanceMatch[];
  total: number;
  hasMore: boolean;
  isLoading: boolean;
  isVerifying: boolean;
  onLoadMore: () => void;
  onBulkVerify: (
    companyId: string,
    decisions: BulkVerifyDecision[],
  ) => Promise<{ processed: number; autoRejectedTotal: number }>;
}

const BatchReviewPanel: React.FC<BatchReviewPanelProps> = ({
  companyId,
  matches,
  total,
  hasMore,
  isLoading,
  isVerifying,
  onLoadMore,
  onBulkVerify,
}) => {
  // Bulk verification is default-denied to a broker-restricted admin at the
  // callable chokepoint; disable the two run buttons rather than let them 403.
  const { isBrokerFilteredAdmin } = useAuth();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [relByMatch, setRelByMatch] = useState<Record<string, RelationshipType>>({});
  const [outcomeByMatch, setOutcomeByMatch] = useState<Record<string, OutcomeType>>({});

  // Clear tier first, then highest confidence within each tier.
  const sorted = useMemo(() => {
    return [...matches].sort((a, b) => {
      const ca = isClearMatch(a) ? 0 : 1;
      const cb = isClearMatch(b) ? 0 : 1;
      if (ca !== cb) return ca - cb;
      return (b.confidenceScore ?? 0) - (a.confidenceScore ?? 0);
    });
  }, [matches]);

  const clearCount = useMemo(() => sorted.filter(isClearMatch).length, [sorted]);
  const firstTailIndex = clearCount; // index where the "closer look" tier begins

  const relFor = (m: RefinanceMatch): RelationshipType =>
    relByMatch[m.matchId] ?? m.suggestedRelationshipType ?? 'refinance';
  const outcomeFor = (m: RefinanceMatch): OutcomeType =>
    outcomeByMatch[m.matchId] ?? 'win_back';

  const allSelected = sorted.length > 0 && sorted.every((m) => selected.has(m.matchId));
  const someSelected = selected.size > 0;

  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected((prev) =>
      prev.size === sorted.length ? new Set() : new Set(sorted.map((m) => m.matchId)),
    );

  const selectClearest = () => setSelected(new Set(sorted.filter(isClearMatch).map((m) => m.matchId)));
  const clearSelection = () => setSelected(new Set());

  const runBulk = async (status: 'confirmed' | 'rejected') => {
    if (selected.size === 0 || isVerifying) return;
    const byId = new Map(sorted.map((m) => [m.matchId, m]));
    const decisions: BulkVerifyDecision[] = [];
    for (const id of selected) {
      const m = byId.get(id);
      if (!m) continue;
      if (status === 'rejected') {
        decisions.push({ matchId: id, status });
      } else {
        const rel = relFor(m);
        decisions.push({
          matchId: id,
          status,
          relationshipType: rel,
          // outcomeType applies ONLY to refinance confirms (backend rule).
          outcomeType: rel === 'refinance' ? outcomeFor(m) : undefined,
        });
      }
    }
    if (decisions.length === 0) return;

    try {
      const { processed, autoRejectedTotal } = await onBulkVerify(companyId, decisions);
      const verb = status === 'confirmed' ? 'Confirmed' : 'Rejected';
      toast.success(
        autoRejectedTotal > 0
          ? `${verb} ${processed} match${processed === 1 ? '' : 'es'} (${autoRejectedTotal} duplicate${
              autoRejectedTotal === 1 ? '' : 's'
            } auto-rejected)`
          : `${verb} ${processed} match${processed === 1 ? '' : 'es'}`,
      );
      clearSelection();
    } catch (err: any) {
      toast.error(err?.message || 'Batch update failed. No partial changes were hidden.');
    }
  };

  if (isLoading && matches.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-12 border border-gray-700 text-center text-gray-400">
        Loading pending matches...
      </div>
    );
  }

  if (!isLoading && matches.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-12 border border-gray-700 text-center">
        <CheckCircle className="w-12 h-12 text-green-500/70 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-300 mb-2">Nothing to review</h3>
        <p className="text-gray-500">There are no pending matches in this view.</p>
      </div>
    );
  }

  const th = 'px-3 py-2 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide';
  const td = 'px-3 py-2 text-sm align-middle';

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 bg-gray-800 rounded-lg p-3 border border-gray-700">
        <button
          type="button"
          onClick={selectClearest}
          disabled={clearCount === 0}
          className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Sparkles className="w-4 h-4" />
          Select clearest ({clearCount})
        </button>
        <button
          type="button"
          onClick={toggleAll}
          className="px-3 py-2 text-sm rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-100 font-medium transition-colors"
        >
          {allSelected ? 'Deselect all' : `Select all (${sorted.length})`}
        </button>
        <div className="ml-auto text-sm text-gray-400">
          {selected.size > 0 ? `${selected.size} selected` : `Showing ${matches.length} of ${total} pending`}
        </div>
      </div>

      {/* Dense table */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-700">
          <thead className="bg-gray-900/60">
            <tr>
              <th className={`${th} w-10`}>
                <input
                  type="checkbox"
                  aria-label="Select all"
                  checked={allSelected}
                  onChange={toggleAll}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-cyan-500 focus:ring-cyan-500"
                />
              </th>
              <th className={th}>Client (discharged &rarr; new)</th>
              <th className={th}>Days</th>
              <th className={th}>Balance &rarr; New loan</th>
              <th className={th}>Lender</th>
              <th className={th}>Broker</th>
              <th className={th}>Conf.</th>
              <th className={th}>Type</th>
              <th className={th}>Relationship</th>
              <th className={th}>Outcome</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/70">
            {sorted.map((m, idx) => {
              const rel = relFor(m);
              const isSel = selected.has(m.matchId);
              const clear = isClearMatch(m);
              return (
                <React.Fragment key={m.matchId}>
                  {idx === firstTailIndex && firstTailIndex > 0 && (
                    <tr className="bg-gray-900/40">
                      <td colSpan={10} className="px-3 py-1.5 text-xs font-semibold text-amber-300/90 uppercase tracking-wide">
                        Needs a closer look (partial name or same-day) &mdash; review before confirming
                      </td>
                    </tr>
                  )}
                  <tr className={isSel ? 'bg-cyan-500/10' : 'hover:bg-gray-700/40'}>
                    <td className={td}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${m.discharged.customerName || m.matchId}`}
                        checked={isSel}
                        onChange={() => toggleOne(m.matchId)}
                        className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-cyan-500 focus:ring-cyan-500"
                      />
                    </td>
                    <td className={td}>
                      <div className="text-gray-100 font-medium">{m.discharged.customerName || '—'}</div>
                      <div className="text-gray-400 flex items-center gap-1">
                        <ChevronRight className="w-3 h-3 text-gray-600" />
                        {m.newLoan.customerName || '—'}
                      </div>
                    </td>
                    <td className={`${td} text-gray-300 tabular-nums`}>{m.daysBetween}</td>
                    <td className={`${td} tabular-nums`}>
                      <div className="text-gray-300">{formatCurrency(m.discharged.balanceAtDischargeCents || 0)}</div>
                      <div className="text-cyan-300">{formatCurrency(m.newLoan.loanAmountCents || 0)}</div>
                    </td>
                    <td className={`${td} text-gray-300`}>
                      <div>{m.discharged.lender || '—'}</div>
                      <div className="text-gray-400">{m.newLoan.lender || '—'}</div>
                    </td>
                    <td className={`${td} text-gray-300`}>
                      {m.discharged.brokerDisplay || m.newLoan.brokerDisplay || '—'}
                    </td>
                    <td className={`${td} tabular-nums`}>
                      <span
                        className={
                          m.confidenceScore >= 80
                            ? 'text-green-400'
                            : m.confidenceScore >= 60
                              ? 'text-yellow-400'
                              : 'text-orange-400'
                        }
                      >
                        {Math.round(m.confidenceScore)}%
                      </span>
                    </td>
                    <td className={td}>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          clear ? 'bg-green-500/10 text-green-300' : 'bg-gray-700 text-gray-300'
                        }`}
                      >
                        {m.matchType}
                      </span>
                    </td>
                    <td className={td}>
                      <div className="inline-flex rounded-md overflow-hidden border border-gray-600">
                        <button
                          type="button"
                          onClick={() => setRelByMatch((p) => ({ ...p, [m.matchId]: 'refinance' }))}
                          className={`flex items-center gap-1 px-2 py-1 text-xs ${
                            rel === 'refinance' ? 'bg-cyan-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                        >
                          <Repeat className="w-3 h-3" /> Refi
                        </button>
                        <button
                          type="button"
                          onClick={() => setRelByMatch((p) => ({ ...p, [m.matchId]: 'returning_client' }))}
                          className={`flex items-center gap-1 px-2 py-1 text-xs ${
                            rel === 'returning_client' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                        >
                          <Users className="w-3 h-3" /> Return
                        </button>
                      </div>
                    </td>
                    <td className={td}>
                      {rel === 'refinance' ? (
                        <select
                          value={outcomeFor(m)}
                          onChange={(e) =>
                            setOutcomeByMatch((p) => ({ ...p, [m.matchId]: e.target.value as OutcomeType }))
                          }
                          className="bg-gray-700 border border-gray-600 rounded-md px-2 py-1 text-xs text-white focus:ring-2 focus:ring-cyan-500"
                        >
                          {OUTCOME_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-xs text-gray-500">—</span>
                      )}
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={isLoading}
            className="px-6 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-medium border border-gray-600 transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Loading...' : `Load more (${matches.length} of ${total})`}
          </button>
        </div>
      )}

      {/* Sticky bulk action bar */}
      {someSelected && (
        <div className="sticky bottom-4 z-10">
          <div className="flex flex-wrap items-center gap-3 bg-gray-900 rounded-lg p-3 border border-gray-600 shadow-lg shadow-black/40">
            <span className="text-sm text-gray-200 font-medium">
              {selected.size} match{selected.size === 1 ? '' : 'es'} selected
            </span>
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={clearSelection}
                disabled={isVerifying}
                className="px-3 py-2 text-sm rounded-lg text-gray-300 hover:text-white transition-colors disabled:opacity-40"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => runBulk('rejected')}
                disabled={isBrokerFilteredAdmin || isVerifying}
                title={isBrokerFilteredAdmin ? BROKER_FILTERED_ADMIN_COPY : undefined}
                className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <XCircle className="w-4 h-4" />
                Reject selected
              </button>
              <button
                type="button"
                onClick={() => runBulk('confirmed')}
                disabled={isBrokerFilteredAdmin || isVerifying}
                title={isBrokerFilteredAdmin ? BROKER_FILTERED_ADMIN_COPY : undefined}
                className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <CheckCircle className="w-4 h-4" />
                {isVerifying ? 'Working...' : 'Confirm selected'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchReviewPanel;
